import React, { memo, useCallback, useMemo } from 'react';
import { cn } from '../../../../helpers/util';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { addNode, selectShowControlsInSingleRow } from '../../../../redux/slices/diagram';
import PlusIcon from '../../edge/icons/plus-icon.component';

interface AddEscalationFactorControlProps {
  id: string;
  controlRecordId?: number;
  index: number;
  disabled: boolean;
}

/**
 * A control component that renders a plus icon for adding an escalation factor.
 *
 * @component
 * @returns {React.JSX.Element} A rendered plus icon control
 *
 * @example
 * ```tsx
 * <div className="bt-group">
 *   <Node {...props} />
 *   <AddEscalationFactorControl />
 * </div>
 * ```
 */
const AddEscalationFactorControl = ({
  id,
  controlRecordId,
  index = 0,
  disabled,
}: AddEscalationFactorControlProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const showControlsInSingleRow = useAppSelector(selectShowControlsInSingleRow);

  const isAboveEdge = useMemo(() => {
    const modY = showControlsInSingleRow ? 0 : index % 2;
    return modY !== 0;
  }, [index, showControlsInSingleRow]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) {
        return;
      }

      dispatch(
        addNode({
          type: 'escalation-factor-node',
          data: { id: crypto.randomUUID(), controlNodeId: id, controlRecordId: controlRecordId },
        })
      );
    },
    [disabled, dispatch, id, controlRecordId]
  );

  const className = cn(
    'nopan nodrag bt-invisible bt-absolute bt-left-[calc(50%-10px)] bt-z-10 bt-cursor-pointer group-focus-within:bt-visible group-hover:bt-visible',
    { 'bt-hidden': disabled },
    { '-bt-bottom-4': isAboveEdge },
    { '-bt-top-4': !isAboveEdge }
  );

  return (
    <div
      data-cy="add-escalation-factor-control"
      title="Add Escalation Factor"
      className={className}
      role="button"
      onClick={handleClick}
    >
      <PlusIcon className="bt-fill-cline bt-stroke-cline bt-text-mono-1" />
    </div>
  );
};

export default memo(AddEscalationFactorControl);
